import { useState } from "react";
import { Icon, TextField } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, ActiveButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useTeamValue } from "providers/TeamContext";
import { InboundHeaderCard } from "ui-components/Layout";
import MDTypography from "components/MDTypography";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import { getBillingInvoiceStatusCount } from "services/data.billing.service";
import { getBillingInvoiceList } from "services/data.billing.service";
import { deactiveBillingInvoice } from "services/data.billing.service";
import { activeBillingInvoice } from "services/data.billing.service";
import { useCustomerValue } from "providers/CustomerContext";
import { generateBillingInvoice } from "services/data.billing.service";
import dayjs from "dayjs";
import { getBillingInvoiceEnquiry } from "services/data.report.service";
import { getBillingInvoiceCustomerDebtorCode } from "services/data.billing.service";
import { PrintButton } from "ui-components/IconButton";

function BillingInvoiceList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = Boolean(customerCode);
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "Inactive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["invoice-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Billing Invoice"]) &&
      permissions["Billing Invoice"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Billing Invoice"]) &&
      permissions["Billing Invoice"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Billing Invoice"]) &&
      permissions["Billing Invoice"].findIndex((b) => b === "deactivate") !== -1);
  const viewPricePermission =
  (Boolean(permissions["Team Role"]) && permissions["Team Role"].some(a => a === "access"))||
     (Boolean(permissions["Billing Invoice"]) &&
      permissions["Billing Invoice"].findIndex((b) => b === "viewPrice") !== -1);

  useQuery({
    queryKey: ["invoice-list", { status, customerCode }],
    queryFn: () => getBillingInvoiceList({ status, customerCode }),
    onSuccess: (data) => {
      setData(
        data.data.data
          .map((x) => {
            return { billFrTo: `${x.billDtFr} ~ ${x.billDtTo}`, ...x };
          })
          .sort((a, b) => b.invoiceNumber.localeCompare(a.invoiceNumber))
      );
    },
    enabled,
    onError,
  });

  useQuery({
    queryKey: ["invoice-list", "status", { customerCode }],
    queryFn: () => getBillingInvoiceStatusCount({ customerCode }),
    onSuccess: (data) => setTabs(data.data.data),
    enabled,
    onError,
  });

  const delMutation = useMutation(deactiveBillingInvoice, { onSuccess, onError });
  const activeMutation = useMutation(activeBillingInvoice, { onSuccess, onError });

  const onEdit = (invoiceNumber) =>
    navigate("/billing/billing-invoice/edit", { state: { invoiceNumber } });

  const onDelete = (invoiceNumber) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${invoiceNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate({ invoiceNumber });
          setDialog(null);
        }}
      />
    );
  };

  const onActive = (invoiceNumber) => {
    setDialog(
      <MessageDialog
        title={`Activate ${invoiceNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate({ invoiceNumber });
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Invoice Number", accessorKey: "invoiceNumber" },
    { header: "Billing Period", accessorKey: "billingPeriod" },
    { header: "Invoice Date", accessorKey: "invoiceDate" },
    { header: "Bill Date", accessorKey: "billFrTo" },
    { header: "Created By", accessorKey: "createdBy" },
    { header: "Created On", accessorKey: "createdOn" },
    // { header: "Phone No", accessorKey: "phoneNo" },
    // { header: "Fax No", accessorKey: "faxNo" },
    // { header: "Email", accessorKey: "email" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { invoiceNumber } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit billing invoice"
                disabled={!editPermission}
                onClick={() => onEdit(invoiceNumber)}
              />
              <DelButton
                title="deactivate billing invoice"
                disabled={!deactivatePermission}
                onClick={() => onDelete(invoiceNumber)}
              />
              <PrintButton
                title="print billing invoice"
                onClick={() => handleDownload(invoiceNumber)}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate billing"
              disabled={!editPermission}
              onClick={() => onActive(invoiceNumber)}
            />
          );
        }
      },
    },
  ];

  let currentDate = new Date();

  const [billingPeriod, setBillingPeriod] = useState(null);
  const [dateEnd, setDateEnd] = useState(new Date(currentDate));
  const [dateStart, setDateStart] = useState(new Date(currentDate.setDate(1)));

  const generateBillingInvoiceMutation = useMutation(generateBillingInvoice, {});

  const customProps = {
    startDate: dateStart,
    setStartDate: setDateStart,
    endDate: dateEnd,
    setEndDate: setDateEnd,
  };

  const handleSubmit = () => {
    if (isSubmitting) return; // Prevent double click
    setIsSubmitting(true);
    window.scrollTo(0, 0);

    var req = {
      customerCode,
      startDateStr: dayjs(dateStart).format("YYYY-MM-DD"),
      endDateStr: dayjs(dateEnd).format("YYYY-MM-DD"),
    };

    generateBillingInvoiceMutation.mutate(req, {
      onSuccess: (data) => {
        setAlert(<Success data={data} onChange={setAlert} />);
        queryClient.invalidateQueries(["invoice-list"]);
        setIsSubmitting(false);
      },
      //onError,
      onError: (error) => {
        onError(error);
        setIsSubmitting(false); // Reset submitting state on error
      },
    });
  };

  const handleDownload = async (invoiceNumber) => {
    const formattedInvoiceNumber = invoiceNumber.replaceAll("/", "");
    setAlert(null);
    const debtorRes = await getBillingInvoiceCustomerDebtorCode(customerCode);

    const debtorCode = debtorRes?.data?.data || "UNKNOWN";
    
    try {
      const response = await getBillingInvoiceEnquiry({
        customerCode,
        startDate:dateStart,
        endDate: dateEnd,
        invoiceNumber,
        viewPricePermission,
      });
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${customerCode}_${formattedInvoiceNumber}_${debtorCode}_${dayjs().format("YYYYMMDDHHmmss")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setAlert(<Error_ content="Error exporting Excel file" onChange={setAlert} />);
    }
  };

  return (
    <ListLayout>
      {dialog}
      {alert}
      <div style={{ marginTop: "1rem" }}>
        <InboundHeaderCard>
          <MDBox>
            <MDTypography color="info" variant="h3">
              Generate Billing Invoice
            </MDTypography>
            <MDBox mt={2}>
              <CustomDateRangePicker {...customProps} />
            </MDBox>
            {/* <MDBox mt={2}>
            <TextField
              label="Billing Period"
              fullWidth
              required
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
            />
          </MDBox> */}

            <MDBox mt={2} display="flex" justifyContent="center">
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSubmit}
                disabled={!addPermission || !(Boolean(dateStart) && Boolean(dateEnd))}
              >
                {isSubmitting ? "Generating" : "Generate"}
                {/* Generate */}
              </MDButton>
            </MDBox>
          </MDBox>
        </InboundHeaderCard>
      </div>

      <MDBox my={3}>
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default BillingInvoiceList;
